<template>
  <div>
    <!-- Breadcrumb -->
    <nav v-if="breadcrumb" class="border-b border-gray-300 py-2 px-2 mb-4 md:px-0">
      <div class="flex justify-between items-center">
        <div>
          <div class="hidden md:flex text-sx">
            <router-link :to="{ name: 'home' }">Home</router-link>
            <span class="mx-2">/</span>
            <router-link :to="{ name: 'organization_details', params: { orgId } }">
              Organization
            </router-link>
            <span class="mx-2">/</span>
            <router-link :to="{ name: 'customers' }">Devices</router-link>
          </div>
          <div class="text-xl text-primary">User devices</div>
        </div>
      </div>
    </nav>

    <!-- Table -->
    <div class="pb-4">
      <div class="bg-white pt-3 border rounded shadow-sm">
        <div class="font-semibold mx-2 text-gray-600">{{ title }}</div>

        <!-- Filters -->
        <div class="flex justify-between pt-2 px-3">
          <div>
            <label class="block tracking-wide text-gray-700">Records</label>
            <select
              v-model="recordsPerPage"
              @change="loadStats(currentPage)"
              class="block appearance-none w-full bg-white border border-gray-300 px-4 py-2 rounded leading-tight focus:outline-none focus:border-gray-500"
            >
              <option>10</option>
              <option>25</option>
              <option>50</option>
              <option>100</option>
              <option>250</option>
            </select>
          </div>

          <div>
            <label class="block tracking-wide text-gray-700">Search</label>
            <input
              v-model="searchString"
              @change="loadStats(currentPage)"
              class="appearance-none block w-full text-gray-700 border border-gray-300 rounded py-2 px-4 leading-tight focus:outline-none focus:border-gray-500"
              type="text"
              placeholder="Search"
            />
          </div>
        </div>

        <!-- Table Data -->
        <div class="overflow-auto">
          <table v-if="!loading" class="table-auto w-full">
            <thead>
              <tr class="border-b font-bold text-sm">
                <td class="px-4 py-2">#</td>
                <td class="px-4 py-2">Name</td>
                <td class="px-4 py-2">OS</td>
                <td class="px-4 py-2">Type</td>
                <td class="px-4 py-2">Customer</td>
                <td class="px-4 py-2">Mac Address</td>
                <td class="px-4 py-2">Connections</td>
                <td class="px-4 py-2">Last seen</td>
                <td class="px-4 py-2">Consumption</td>
                <td class="px-4 py-2">Status</td>
                <td class="px-4 py-2 text-center">Actions</td>
              </tr>
            </thead>

            <tbody>
              <tr
                class="border-b hover:bg-gray-50"
                v-for="(device, index) in organization.devices || []"
                :key="device.id || index"
              >
                <td class="px-4 py-2">{{ index + 1 }}</td>
                <td class="px-4 py-2">{{ device.name }}</td>
                <td class="px-4 py-2">{{ device.os }} {{ device.os_version }}</td>
                <td class="px-4 py-2">{{ device.is_mobile ? "Mobile" : "Non Mobile" }}</td>
                <td class="px-4 py-2">
                  <router-link
                    v-if="device.customer"
                    :to="{ name: 'customer_profile', params: { customerId: device.customer.id } }"
                  >
                    {{ device.customer.identity }}
                  </router-link>
                </td>
                <td class="px-4 py-2 font-mono">{{ device.mac_address }}</td>
                <td class="px-4 py-2">{{ device.connectionsCount }}</td>
                <td class="px-4 py-2">{{ formatDate(device.last_seen, "DD-MM-YYYY HH:mm") }}</td>
                <td class="px-4 py-2">{{ device.usage }}</td>

                <!-- Online / Offline -->
                <td
                  class="px-4 py-2 flex items-center"
                  :class="device.isOnline ? 'text-green-700' : 'text-red-700'"
                >
                  <feather :type="device.isOnline ? 'wifi' : 'wifi-off'" size="16" class="mr-1" />
                  <span>{{ device.isOnline ? "Online" : "Offline" }}</span>
                </td>

                <!-- Block button -->
                <td class="px-4 py-2 text-center">
                  <span
                    v-if="device.is_blocked"
                    class="bg-red-100 text-red-700 px-3 py-1 rounded text-sm font-semibold"
                  >
                    Blocked
                  </span>

                  <button
                    v-if="!device.is_blocked"
                    @click="blockDevice(device)"
                    class="bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-1 px-3 rounded transition"
                  >
                    Block
                  </button>

                  <button
                    v-else
                    @click="unBlockDevice(device)"
                    class="bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-1 px-3 rounded transition"
                  >
                    Unblock
                  </button>
                </td>

              </tr>
            </tbody>
          </table>

          <!-- Loading State -->
          <div v-else class="py-6 text-center w-full">
            <feather animation="spin" type="loader" size="32" class="mr-4" />
            <p>Loading...</p>
          </div>

          <!-- Pagination -->
          <Pagination
            class="py-2 px-3"
            :total-count="organization.devicesCount || 0"
            :current-page="currentPage"
            :tag="'connections'"
            :offset="offset"
            :records-count="organization.devices.length || 0"
            :click-handler="loadStats"
            :records-per-page="parseInt(recordsPerPage)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import GraphQl from "../../services/graphql.service";
import Pagination from "@/components/Pagination";
import moment from "moment";

export default {
  name: "Devices",
  components: { Pagination },
  data() {
    return {
      searchString: "",
      recordsPerPage: 10,
      organization: { devices: [], devicesCount: 0 },
      orgId: this.$store.getters.orgId,
      currentPage: 1,
      loading: true,
    };
  },
  props: {
    breadcrumb: { default: true, type: Boolean },
    title: { type: String, default: "Connected devices" },
    configs: Object,
    customers: { type: Array, default: () => [] },
    venues: { type: Array, default: () => [] },
    accessPoints: { type: Array, default: () => [] },
    online: { type: Boolean, default: null },
    rowsPerPage: { type: Number, default: 10 },
    authorized: { type: Boolean, default: null },
  },
  computed: {
    offset() {
      return (this.currentPage - 1) * this.recordsPerPage;
    },
  },
  mounted() {
    this.loadStats();
  },
  methods: {
    formatDate(timestamp, format) {
      return timestamp ? moment(timestamp).format(format) : "-";
    },

    loadStats(newPage) {
      this.currentPage = newPage || 1;
      this.loading = true;

      const params =
        (this.venues.length ? `venues:["${this.venues.join('","')}"],` : "") +
        (this.customers.length ? `customers:["${this.customers.join('","')}"],` : "") +
        (this.accessPoints.length ? `accessPoints:["${this.accessPoints.join('","')}"],` : "") +
        (this.online != null ? `online:${this.online},` : "") +
        (this.authorized != null ? `authorized:${this.authorized},` : "") +
        `limit:${this.recordsPerPage}, offset:${this.offset}, order:"created_at desc", search:"${this.searchString}"`;

      GraphQl.sendRequest(`
        {
          organization(id: "${this.$store.getters.orgId}") {
            devicesCount(${params})
            devices(${params}) {              
              mac_address
                brand
                name
                browser
                created_at
                model
                os
                os_version
                is_blocked
                is_mobile
                customer_id
                connected_sessions
                connectionsCount
                usage
              customer { id identity }
            }
          }
        }
      `)
        .then((response) => {
          if (response && response.organization) {
            this.organization = response.organization;
          }
        })
        .catch((err) => {
          console.error("Error loading devices:", err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async blockDevice(device) {
    if (!confirm(`Are you sure you want to block device ${device.name || device.mac_address}?`)) return;

    this.loading = true;
    try {
      const response = await axios.post(
        "https://apiresources.brandfi.co.ke/api/v1/device/blockdevice",
        { device_mac: device.mac_address },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data && response.data.message) {
        alert(response.data.message);
      } else {
        alert("Device blocked successfully!");
      }

      // Reload the updated data
      this.loadStats(this.currentPage);
    } catch (error) {
      console.error("Error blocking device:", error);
      alert("Failed to block device.");
    } finally {
      this.loading = false;
    }
  },

  async unBlockDevice(device) {
    if (!confirm(`Are you sure you want to unblock device ${device.name || device.mac_address}?`)) return;

    this.loading = true;
    try {
      const response = await axios.post(
        "https://apiresources.brandfi.co.ke/api/v1/device/unblockdevice",
        { device_mac: device.mac_address },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data && response.data.message) {
        alert(response.data.message);
      } else {
        alert("Device unblocked successfully!");
      }

      this.loadStats(this.currentPage);
    } catch (error) {
      console.error("Error unblocking device:", error);
      alert("Failed to unblock device.");
    } finally {
      this.loading = false;
    }
  },


  },
};
</script>

<style scoped>
button[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
